import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Car, CheckCheck, ChevronDown, ChevronUp, Circle, CircleCheck, Package, Phone, X,
} from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { getProductTypeName } from '@/lib/constants';
import DispatchLoadSheet from '@/components/phone/DispatchLoadSheet';
import {
  AppHeader, ChipRow, EmptyState, ScreenLoader, Spinner, StatusPill,
} from '@/components/phone/primitives';
import { Colors, pcs } from '@/components/phone/theme';

/**
 * Loading a van, from the floor.
 *
 * The desktop Dispatch page builds a load in a rail beside the list. A phone has
 * no rail, so the list itself is the picker — tick the orders going out, and a
 * bar appears at the thumb saying what is on the van with the one button that
 * sends it. Vehicle and driver are asked for once, on the sheet that button
 * opens, and not before: a supervisor ticking orders has not decided which van
 * yet.
 *
 * Four views, because a trip has four ages and only two of them are decisions.
 * To pack is boxing; To send is loading; On the road is what to press when a
 * driver rings in; Done is a record and has no buttons at all.
 *
 * A ticked order opens its pieces: filled in with everything that is ready, and
 * typed over when only part of it fits. 10,000 boxes are packed, 5,000 go today,
 * and the other 5,000 are on this list again tomorrow.
 *
 * To pack comes before all of it, because Packing is the module's own stage.
 * Every pipeline the plant runs ends with it, so it could never say whose work a
 * job was — on the supervisor's board it matched the whole factory. Here it is
 * what it actually is: goods off the machines, waiting to be boxed, one press
 * from the loading list underneath.
 *
 * Mirrors `mobile-frontend/app/(tabs)/dispatch.tsx`.
 */

/** What is on this order, named the way the floor names it. */
function itemsLabel(order) {
  const names = (order.ready_items || []).map((i) => i.item_name).filter(Boolean);
  if (names.length === 0) return order.customer_name || 'Order';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

/** The type codes on a set of rows, in the order they read. */
const typesOn = (rows) =>
  Array.from(new Set(rows.flatMap((r) => r.product_types || []))).sort();

export default function SupervisorDispatchPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState('ready');
  const [readyOrders, setReadyOrders] = useState([]);
  const [packingOrders, setPackingOrders] = useState([]);
  // '' is every type he covers; the chips only appear when he covers more than one
  const [typeFilter, setTypeFilter] = useState('');
  const [packing, setPacking] = useState(null);
  const [active, setActive] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [picked, setPicked] = useState([]);
  // only the lines somebody typed a smaller number on; the rest sends all of
  // what is ready, which is the ordinary evening
  const [partQty, setPartQty] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [delivering, setDelivering] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [ready, toPack, road, done, vans, men, unread] = await Promise.all([
        api.get('/dispatch/ready-orders'),
        api.get('/dispatch/packing-queue'),
        api.get('/dispatch/dispatches', { params: { status: 'active' } }),
        api.get('/dispatch/dispatches', { params: { status: 'completed' } }),
        api.get('/vehicles', { params: { status: 'active', available: true } }),
        api.get('/drivers', { params: { status: 'active', available: true } }),
        api.get('/notifications/unread-count'),
      ]);
      const readyList = ready.data || [];
      setReadyOrders(readyList);
      setPackingOrders(toPack.data || []);
      setActive(road.data.dispatches || []);
      setCompleted(done.data.dispatches || []);
      setVehicles(vans.data || []);
      setDrivers(men.data || []);
      setUnreadCount(unread.data.count || 0);
      // an order somebody else loaded while this screen was open must not stay
      // ticked on it — it is not on the loading list any more
      setPicked((prev) => prev.filter((id) => readyList.some((o) => o.id === id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the dispatch lists');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePick = (orderId) => {
    setPicked((prev) =>
      (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]));
    // an order taken off the load forgets the smaller numbers typed on it
    setPartQty((prev) => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  /** How many pieces of this line are going — all that is ready unless typed over. */
  const goingQty = useCallback((order, item) => {
    const typed = partQty[order.id]?.[item.id];
    if (typed === undefined) return item.ready_quantity ?? item.quantity ?? 0;
    return typed === '' ? 0 : Number(typed);
  }, [partQty]);

  const setGoingQty = (order, item, value) => {
    const ready = item.ready_quantity ?? item.quantity ?? 0;
    const digits = String(value).replace(/\D/g, '');
    const cleaned = digits === '' ? '' : String(Math.min(ready, Number(digits)));
    setPartQty((prev) => ({ ...prev, [order.id]: { ...prev[order.id], [item.id]: cleaned } }));
  };

  const sendAll = (order) => setPartQty((prev) => {
    const next = { ...prev };
    delete next[order.id];
    return next;
  });

  const orderGoing = useCallback(
    (order) => (order.ready_items || []).reduce((sum, item) => sum + goingQty(order, item), 0),
    [goingQty],
  );

  const pickedOrders = useMemo(
    () => readyOrders.filter((o) => picked.includes(o.id)),
    [readyOrders, picked],
  );
  // a load is counted in items and pieces, not orders — half an order can go
  const pickedItems = pickedOrders.reduce((n, o) => n + (o.ready_item_count || 0), 0);
  const pickedQty = pickedOrders.reduce((n, o) => n + orderGoing(o), 0);

  const dispatchLoad = async (choice) => {
    // only an order that has pieces to count can be set to nothing by mistake;
    // one booked without a count has no number to type and goes as it is
    const empty = pickedOrders.find((o) => (o.ready_quantity || 0) > 0 && orderGoing(o) <= 0);
    if (empty) {
      toast.error(`${itemsLabel(empty)} is set to 0 — type how many are going`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/dispatch/dispatches', {
        ...choice,
        order_ids: picked,
        // what is going, line by line; the rest stays standing at Dispatch
        loads: pickedOrders.map((o) => ({
          order_id: o.id,
          items: (o.ready_items || [])
            .map((item) => ({
              item_id: item.id,
              quantity: goingQty(o, item),
              ready: item.ready_quantity ?? item.quantity ?? 0,
            }))
            // a line set to 0 stays behind; a line with no count at all is 0
            // because nobody typed one, and it goes
            .filter((line) => line.quantity > 0 || line.ready === 0)
            .map(({ item_id, quantity }) => ({ item_id, quantity })),
        })),
      });
      setSheetOpen(false);
      setPicked([]);
      setPartQty({});
      toast.success(`${res.data.dispatch_number} booked — ${choice.vehicle_number}`);
      setView('road');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not book the trip');
    }
    setSubmitting(false);
  };

  const addVehicle = async (body) => {
    try {
      const res = await api.post('/vehicles', body);
      setVehicles((prev) => [...prev, res.data]);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add the vehicle');
      return null;
    }
  };

  const addDriver = async (body) => {
    try {
      const res = await api.post('/drivers', body);
      setDrivers((prev) => [...prev, res.data]);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add the driver');
      return null;
    }
  };

  /**
   * Boxing is a yes/no: the goods are packed or they are not. One press sends
   * everything of the order standing at Packing on to the loading list, and the
   * row moves from To pack to To send. A part-packed run is a different decision
   * and still lives on the job card, where the quantity can be typed.
   */
  const markPacked = async (order) => {
    if (packing) return;
    setPacking(order.id);
    try {
      const res = await api.post(`/supervisor/orders/${order.id}/packed`);
      toast.success(res.data?.message || t('dispatch.packed'));
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not mark it packed');
    }
    setPacking(null);
  };

  const markDelivered = async (dispatchId, orderId, who) => {
    setDelivering(`${dispatchId}:${orderId}`);
    try {
      await api.put(`/dispatch/dispatches/${dispatchId}/orders/${orderId}/deliver`);
      toast.success(`${who} marked delivered`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark it delivered');
    }
    setDelivering(null);
  };

  const trips = view === 'done' ? completed : active;

  // the types across both working lanes, so the chip row does not change shape
  // when the lane is switched
  const types = useMemo(
    () => typesOn([...packingOrders, ...readyOrders]),
    [packingOrders, readyOrders],
  );
  const onType = useCallback(
    (rows) => (typeFilter ? rows.filter((r) => (r.product_types || []).includes(typeFilter)) : rows),
    [typeFilter],
  );
  const visiblePacking = useMemo(() => onType(packingOrders), [onType, packingOrders]);
  const visibleReady = useMemo(() => onType(readyOrders), [onType, readyOrders]);

  const lanes = [
    { key: 'pack', label: t('dispatch.to_pack'), count: visiblePacking.length },
    { key: 'ready', label: t('dispatch.to_send'), count: visibleReady.length },
    { key: 'road', label: t('dispatch.on_the_road'), count: active.length },
    { key: 'done', label: t('dispatch.done'), count: completed.length },
  ];

  return (
    <div className="flex flex-1 flex-col px-5">
      <AppHeader
        greeting={t('nav.dispatch')}
        subtitle={user?.full_name || ''}
        unreadCount={unreadCount}
        onNotifications={() => navigate('/supervisor/notifications')}
        testID="notifications-btn"
      />

      <ChipRow
        chips={lanes.map((l) => ({ key: l.key, label: `${l.label}${l.count ? ` ${l.count}` : ''}` }))}
        active={view}
        onSelect={(key) => { setView(key); setExpanded(null); }}
        testIDPrefix="dispatch-chip"
      />

      {/* one line, or several — a supervisor covering only offset never sees this */}
      {types.length > 1 && (view === 'pack' || view === 'ready') && (
        <div className="mt-2">
          <ChipRow
            chips={[{ key: '', label: t('dispatch.all_types') },
              ...types.map((code) => ({ key: code, label: getProductTypeName(code) }))]}
            active={typeFilter}
            onSelect={setTypeFilter}
            testIDPrefix="dispatch-type"
          />
        </div>
      )}

      {loading ? <ScreenLoader /> : (
        <div className={`pb-6 ${view === 'ready' && picked.length ? 'pb-24' : ''}`}>
          {view === 'pack' && (
            visiblePacking.length === 0
              ? <EmptyState icon={Package} message={t('dispatch.nothing_to_pack')} />
              : (
                <ul data-testid="packing-list">
                  {visiblePacking.map((order) => (
                    <PackingRow
                      key={order.id}
                      order={order}
                      t={t}
                      busy={packing === order.id}
                      onOpen={() => navigate(`/supervisor/order/${order.id}`)}
                      onPacked={() => markPacked(order)}
                    />
                  ))}
                </ul>
              )
          )}

          {view === 'ready' && (
            visibleReady.length === 0
              ? <EmptyState icon={Package} message={t('dispatch.nothing_ready')} />
              : (
                <ul data-testid="ready-list">
                  {visibleReady.map((order) => (
                    <ReadyRow
                      key={order.id}
                      order={order}
                      picked={picked.includes(order.id)}
                      going={orderGoing(order)}
                      partQty={partQty[order.id] || {}}
                      onToggle={() => togglePick(order.id)}
                      onQty={(item, value) => setGoingQty(order, item, value)}
                      onSendAll={() => sendAll(order)}
                    />
                  ))}
                </ul>
              )
          )}

          {(view === 'road' || view === 'done') && (
            trips.length === 0
              ? (
                <EmptyState
                  icon={Car}
                  message={view === 'done' ? t('dispatch.no_history') : t('dispatch.nothing_out')}
                />
              )
              : (
                <ul data-testid="trip-list">
                  {trips.map((trip) => (
                    <TripRow
                      key={trip.id}
                      trip={trip}
                      t={t}
                      open={expanded === trip.id}
                      delivering={delivering}
                      onToggle={() => setExpanded(expanded === trip.id ? null : trip.id)}
                      onOpenOrder={(orderId) => navigate(`/supervisor/order/${orderId}`)}
                      onDeliver={markDelivered}
                    />
                  ))}
                </ul>
              )
          )}
        </div>
      )}

      {/* the load, at the thumb — it only exists once something is on it */}
      {view === 'ready' && picked.length > 0 && (
        <div
          className="fixed bottom-[76px] left-1/2 z-30 flex w-[calc(100%-2.5rem)] max-w-[23.5rem] -translate-x-1/2 items-center gap-3 rounded-[14px] px-3 py-2.5 shadow-lg"
          style={{ backgroundColor: Colors.neutral[800] }}
        >
          <button
            type="button"
            data-testid="clear-load"
            onClick={() => { setPicked([]); setPartQty({}); }}
            aria-label="Clear the load"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10"
          >
            <X className="h-[18px] w-[18px]" style={{ color: Colors.text.inverse }} />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold" style={{ color: Colors.text.inverse }}>
              {picked.length} {picked.length === 1 ? 'order' : 'orders'} · {pickedItems}{' '}
              {pickedItems === 1 ? 'item' : 'items'}
            </p>
            <p className="truncate text-[11px]" style={{ color: Colors.neutral[300] }}>{pcs(pickedQty)}</p>
          </div>
          <button
            type="button"
            data-testid="open-load-sheet"
            onClick={() => setSheetOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-2"
            style={{ backgroundColor: Colors.accent.DEFAULT }}
          >
            <Car className="h-[18px] w-[18px]" style={{ color: Colors.text.inverse }} />
            <span className="text-[13px] font-bold" style={{ color: Colors.text.inverse }}>
              {t('dispatch.load_it')}
            </span>
          </button>
        </div>
      )}

      <DispatchLoadSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        summary={`${picked.length} ${picked.length === 1 ? 'order' : 'orders'} · ${pickedItems} ${pickedItems === 1 ? 'item' : 'items'} · ${pcs(pickedQty)}`}
        vehicles={vehicles}
        drivers={drivers}
        submitting={submitting}
        onSubmit={dispatchLoad}
        onAddVehicle={addVehicle}
        onAddDriver={addDriver}
      />
    </div>
  );
}

function PackingRow({ order, t, busy, onOpen, onPacked }) {
  const names = (order.items || []).map((i) => i.item_name).filter(Boolean);
  return (
    <li
      data-testid={`packing-order-${order.id}`}
      className="mt-3 flex items-center gap-3 rounded-[10px] border px-4 py-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {names.length ? `${names[0]}${names.length > 1 ? ` +${names.length - 1}` : ''}` : 'Order'}
        </span>
        <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
          {order.firm_name || order.customer_name || 'Unknown'}
        </span>
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {pcs(order.packing_quantity)} · {order.order_id}
          {order.expected_delivery_date ? ` · ${formatIST(order.expected_delivery_date, { compact: true })}` : ''}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-2">
          <StatusPill label={`${order.item_count} ${order.item_count === 1 ? 'item' : 'items'}`} tone="neutral" />
          {(order.product_types || []).map((code) => (
            <StatusPill key={code} label={getProductTypeName(code)} tone="neutral" />
          ))}
          {order.priority === 'high' && <StatusPill label="Urgent" tone="critical" />}
        </span>
      </button>
      <button
        type="button"
        data-testid={`packed-${order.id}`}
        onClick={onPacked}
        disabled={busy}
        className="flex min-w-[92px] shrink-0 items-center justify-center rounded-[10px] px-3 py-2.5 text-[12px] font-bold disabled:opacity-70"
        style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
      >
        {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : t('dispatch.packed')}
      </button>
    </li>
  );
}

function ReadyRow({ order, picked, going, partQty, onToggle, onQty, onSendAll }) {
  const staying = (order.ready_quantity || 0) - going;
  const Icon = picked ? CircleCheck : Circle;
  return (
    <li
      data-testid={`ready-order-card-${order.id}`}
      className="mt-3 rounded-[10px] border px-4 py-3"
      style={{
        backgroundColor: picked ? Colors.accent.wash : Colors.background.primary,
        borderColor: picked ? Colors.accent.DEFAULT : Colors.border.default,
      }}
    >
      <button
        type="button"
        data-testid={`ready-order-${order.id}`}
        onClick={onToggle}
        className="flex w-full items-start gap-3 text-left"
      >
        <Icon
          className="mt-0.5 h-6 w-6 shrink-0"
          style={{ color: picked ? Colors.accent.DEFAULT : Colors.text.disabled }}
        />
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {itemsLabel(order)}
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
            {order.firm_name || order.customer_name || 'Unknown'}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {pcs(order.ready_quantity)} · {order.order_id}
            {order.expected_delivery_date ? ` · ${formatIST(order.expected_delivery_date, { compact: true })}` : ''}
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill
              label={`${order.ready_item_count} ${order.ready_item_count === 1 ? 'item' : 'items'}`}
              tone="neutral"
            />
            {/* an order is rarely all-or-nothing; the rest is still on a machine
                and loading it now would strand the driver with a part order */}
            {!order.fully_ready && (
              <StatusPill
                label={order.total_item_count > 1
                  ? `${order.ready_item_count} of ${order.total_item_count} ready`
                  : `${pcs(order.ready_quantity)} of ${pcs(order.total_quantity)}`}
                tone="caution"
              />
            )}
            {/* pieces that went on an earlier van, so nobody counts them twice */}
            {!!order.sent_quantity && (
              <StatusPill label={`${pcs(order.sent_quantity)} already sent`} tone="neutral" />
            )}
            {order.priority === 'high' && <StatusPill label="Urgent" tone="critical" />}
          </span>
        </span>
      </button>

      {/* the pieces going, asked for only once the order is on the van */}
      {picked && (
        <div className="mt-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          {(order.ready_items || []).map((item) => {
            const ready = item.ready_quantity ?? item.quantity ?? 0;
            const typed = partQty[item.id];
            return (
              <div key={item.id} className="flex items-center gap-3 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium" style={{ color: Colors.text.primary }}>
                    {item.item_name || 'Item'}
                  </p>
                  <p className="truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {/* booked without a count, so there is no number to ask for */}
                    {ready === 0 ? 'No piece count — it goes as it is' : `of ${pcs(ready)} ready`}
                  </p>
                </div>
                {ready > 0 && (
                  <input
                    data-testid={`load-qty-${item.id}`}
                    className="h-10 w-24 rounded-[10px] border px-2 text-right text-[13px] font-semibold outline-none"
                    style={{
                      backgroundColor: Colors.background.primary,
                      borderColor: Colors.border.default,
                      color: Colors.text.primary,
                    }}
                    value={typed === undefined ? String(ready) : typed}
                    onChange={(e) => onQty(item, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    inputMode="numeric"
                    aria-label={`Pieces of ${item.item_name || 'this item'} going out`}
                  />
                )}
              </div>
            );
          })}
          {staying > 0 && (
            <div className="mt-2 flex items-baseline justify-between gap-3">
              <p className="text-[11px]" style={{ color: Colors.text.secondary }}>
                Sending {pcs(going)} — {pcs(staying)} stay here for the next trip.
              </p>
              <button
                type="button"
                data-testid={`send-all-${order.id}`}
                onClick={onSendAll}
                className="shrink-0 text-[11px] font-bold"
                style={{ color: Colors.accent.DEFAULT }}
              >
                Send all
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function TripRow({ trip, t, open, delivering, onToggle, onOpenOrder, onDeliver }) {
  const delivered = (trip.orders || []).filter((o) => o.delivery_status === 'delivered').length;
  const total = trip.orders?.length || 0;
  return (
    <li
      data-testid={`trip-${trip.id}`}
      className="mt-3 rounded-[10px] border px-4 py-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        data-testid={`trip-head-${trip.id}`}
        onClick={onToggle}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
            {trip.dispatch_number}
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
            {trip.vehicle_number}{trip.is_external_vehicle ? ' (hired)' : ''} · {trip.driver_name}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {delivered} of {total} delivered
            {trip.dispatch_date ? ` · left ${formatIST(trip.dispatch_date, { compact: true })}` : ''}
          </span>
        </span>
        {open
          ? <ChevronUp className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />
          : <ChevronDown className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />}
      </button>

      {open && (
        <div className="mt-3 border-t pt-2" style={{ borderColor: Colors.border.subtle }}>
          {!!trip.driver_mobile && (
            <a
              data-testid={`call-driver-${trip.id}`}
              href={`tel:${trip.driver_mobile}`}
              className="flex items-center gap-1.5 py-1.5 text-[12px] font-semibold"
              style={{ color: Colors.status.success }}
            >
              <Phone className="h-3.5 w-3.5" /> {trip.driver_mobile}
            </a>
          )}
          {(trip.orders || []).map((order) => {
            const isDone = order.delivery_status === 'delivered';
            const busy = delivering === `${trip.id}:${order.id}`;
            const who = order.firm_name || order.customer_name || order.order_id;
            return (
              <div key={order.id} className="flex items-center gap-3 py-2">
                <button type="button" onClick={() => onOpenOrder(order.id)} className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-[13px] font-semibold" style={{ color: Colors.text.primary }}>
                    {order.item_names?.length ? order.item_names[0] : who}
                    {(order.item_names?.length || 0) > 1 ? ` +${order.item_names.length - 1}` : ''}
                  </span>
                  <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {who} · {order.order_id}
                    {order.dispatched_quantity ? ` · ${pcs(order.dispatched_quantity)} sent` : ''}
                  </span>
                  {/* what went short — the question somebody rings up about */}
                  {order.is_part_load && (
                    <span className="block truncate text-[11px] font-medium" style={{ color: Colors.semantic.caution }}>
                      Part load · {pcs(order.pending_quantity)} still to send
                    </span>
                  )}
                  {isDone && !!order.delivered_at && (
                    <span className="block truncate text-[11px]" style={{ color: Colors.status.success }}>
                      Delivered {formatIST(order.delivered_at, { compact: true })}
                      {order.delivered_by_name ? ` · ${order.delivered_by_name}` : ''}
                    </span>
                  )}
                </button>
                {isDone ? (
                  <CheckCheck className="h-5 w-5 shrink-0" style={{ color: Colors.status.success }} />
                ) : (
                  <button
                    type="button"
                    data-testid={`deliver-${trip.id}-${order.id}`}
                    onClick={() => onDeliver(trip.id, order.id, who)}
                    disabled={busy}
                    className="flex min-w-[84px] shrink-0 items-center justify-center rounded-[10px] px-3 py-2 text-[12px] font-bold disabled:opacity-70"
                    style={{ backgroundColor: Colors.status.success, color: Colors.text.inverse }}
                  >
                    {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : t('dispatch.delivered')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}
